/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject, ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { DataState, ItemContext, Range, Metadata, ViewportInfo } from '../UNSAFE_Collection';
import { Collection } from '../PRIVATE_Collection';
import {
  Placeholder,
  PlaceholderContext,
  PlaceholderType,
  PLACEHOLDER_STYLE_CLASS
} from './Placeholder';
import { useViewportIntersect, ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';

/**
 * Props for the VirtualizedCollection component
 */
type Props<K, D> = {
  /**
   * A DataState object that provide information including data and metadata to the VirtualizedCollection.
   */
  data: DataState<K, D> | null;

  /**
   * Renderer callback used to render each item
   */
  children: (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => ComponentChildren;

  /**
   * Callback to calculate placeholder height
   */
  placeholderHeight?: (context: PlaceholderContext) => number;

  /**
   * Viewport configuration including specifying the element which is used to determine what range
   * of data to render
   */
  viewportConfig: ViewportConfig;

  /**
   * The selector string to identify the children items
   */
  itemSelector: string;

  /**
   * Callback function to handle when viewport has changed, including the case
   * where user scrolls to the end of the component and there are more items to load.
   * The function should sets a new DataState on the component for the specified range.
   */
  onLoadRange: (range: Range) => void;

  /**
   * The number of additional items to render before and after the current viewport.  This is
   * mainly used for performance tuning.  Increase the overscan will result in less frequent
   * rendering when scroll but at the same time each render will take longer depending on the
   * item content.
   */
  overscan?: number;

  /**
   * Optionally provide a custom callback to return the range to render based on viewport info
   */
  rangeExtractor?: (info: ViewportInfo) => Range;

  /**
   * Optionally specify the custom load more indicator
   */
  loadMoreIndicator?: ComponentChildren;

  /**
   * Optionally provide the suggestion indicator sparkle
   */
  suggestions?: ComponentChildren;
};

/**
 * A function that returns the default range extractor
 * @param fetchSize
 */
export function getVirtualizedRangeExtractor(fetchSize: number) {
  return function ({ itemSize, viewportHeight, scrollOffset, overscan = 0 }: ViewportInfo): Range {
    // if any of these is missing then return default initial range
    if (
      itemSize === undefined ||
      itemSize === 0 ||
      viewportHeight === undefined ||
      scrollOffset === undefined
    ) {
      return { offset: 0, count: fetchSize };
    }
    const offset = Math.ceil(scrollOffset / itemSize);
    const count = Math.ceil(viewportHeight / itemSize);
    return {
      offset: Math.max(0, offset - overscan),
      count: count + overscan * (offset > 0 ? 2 : 1)
    };
  };
}

/**
 * Helper function to find the top and bottom of an element relative to the viewport
 * @param elem
 */
const getElementTopBottom = (elem: HTMLElement): { top: number; bottom: number } => {
  let top;
  let bottom;
  if (elem === document.documentElement) {
    top = 0;
    bottom = document.documentElement.clientHeight;
  } else {
    const scrollerBounds = elem.getBoundingClientRect();
    top = scrollerBounds.top;
    bottom = scrollerBounds.bottom;
  }
  return { top, bottom };
};

const isDataStateValid = <K, D>(dataState: DataState<K, D>) => {
  return dataState && dataState.data && dataState.offset >= 0 && dataState.totalSize >= 0;
};

const DEFAULT_MARGIN = 1;

/**
 * Returns the range for the current viewport
 */
const getViewportRange = (
  scroller: HTMLElement | null,
  root: HTMLElement,
  overscan: number,
  rangeExtractor: (info: ViewportInfo) => Range,
  estimateItemSize: number
) => {
  let scrollOffset;
  let viewportHeight;

  if (scroller && root) {
    const rootRect = root.getBoundingClientRect();
    const viewportRect = getElementTopBottom(scroller);
    if (rootRect && viewportRect) {
      scrollOffset = viewportRect.top - rootRect.top - DEFAULT_MARGIN;
      viewportHeight = viewportRect.bottom - viewportRect.top + DEFAULT_MARGIN;
    }
  }

  if (scrollOffset !== undefined && viewportHeight !== undefined) {
    // figure out what the new range should be
    const range = rangeExtractor({
      scrollOffset,
      viewportHeight,
      overscan: overscan,
      itemSize: estimateItemSize
    });

    return range;
  }
  return null;
};

const DEFAULT_OVERSCAN = 1;
const DEFAULT_END_INDEX = 25; // match default fetch size
const LOADMORE_STYLE_CLASS = 'oj-collection-loadmore';
const LOAD_MORE_AND_PLACEHOLDER_SELECTOR = `.${LOADMORE_STYLE_CLASS}, .${PLACEHOLDER_STYLE_CLASS}`;
const DEFAULT_RANGE_EXTRACTOR = getVirtualizedRangeExtractor(DEFAULT_END_INDEX);

/**
 * Component that only render items in the specified viewport.
 * @param props
 */
export function VirtualizedCollection<K, D>({
  data,
  children,
  viewportConfig,
  itemSelector,
  placeholderHeight,
  rangeExtractor = DEFAULT_RANGE_EXTRACTOR,
  overscan = DEFAULT_OVERSCAN,
  onLoadRange,
  loadMoreIndicator,
  suggestions
}: Props<K, D>) {
  const rootRef: RefObject<any> = useRef<HTMLElement>(null);
  const estimateItemSizeRef: RefObject<number> = useRef(0);

  // detect if viewport has changed and it needs to re-render based on a new range
  useViewportIntersect(
    viewportConfig,
    DEFAULT_MARGIN,
    0,
    LOAD_MORE_AND_PLACEHOLDER_SELECTOR,
    () => {
      const range = getViewportRange(
        viewportConfig.scroller(),
        rootRef.current,
        overscan,
        rangeExtractor,
        estimateItemSizeRef.current ?? 0
      );
      if (range) {
        onLoadRange(range);
      }
    }
  );

  // calculate average item height.  For now, we'll just do this once, but we can
  // do a rolling average where we'll adjust on every render.
  useEffect(() => {
    if (estimateItemSizeRef.current === 0) {
      let totalHeight = 0;
      const nodes = rootRef.current.querySelectorAll(itemSelector);
      if (nodes.length > 0) {
        nodes.forEach((item: HTMLElement) => {
          totalHeight += item.offsetHeight;
        });
        estimateItemSizeRef.current = totalHeight / nodes.length;
      }
    }
  });

  // override itemRenderer to adjust the index
  const _getItemRenderer = (startIndex: number) => {
    // returns a new render function
    return (context: ItemContext<{ data: D; metadata: Metadata<K> }>) => {
      const itemContext: ItemContext<{ data: D; metadata: Metadata<K> }> = {
        index: startIndex + context.index,
        data: context.data
      };
      return children(itemContext);
    };
  };

  // if DataState is invalid (including not specified), just render blank
  if (!data || !isDataStateValid(data)) {
    return <div ref={rootRef} />;
  }

  const rangeData = data.data;
  const offset = data.offset;
  const endIndex = offset + rangeData.length;
  const itemCount = data.totalSize;
  const topPlaceholderContext =
    offset >= 0
      ? {
          fromIndex: 0,
          toIndex: offset,
          which: 'topPlaceholder' as PlaceholderType,
          estimateItemSize: estimateItemSizeRef.current ?? 0
        }
      : undefined;
  const bottomPlaceholderContext =
    endIndex && endIndex <= itemCount
      ? {
          fromIndex: endIndex,
          toIndex: itemCount,
          which: 'bottomPlaceholder' as PlaceholderType,
          estimateItemSize: estimateItemSizeRef.current ?? 0
        }
      : undefined;

  const renderLoadMore =
    loadMoreIndicator && data.sizePrecision === 'atLeast' && endIndex <= itemCount;

  // might need to support elementType prop in the future to allow customization of what
  // type of element to render for the root
  return (
    <div ref={rootRef} style="will-change: contents">
      {suggestions}
      {topPlaceholderContext && (
        <Placeholder
          context={topPlaceholderContext}
          placeholderHeight={placeholderHeight}></Placeholder>
      )}
      <Collection items={rangeData}>{_getItemRenderer(offset)}</Collection>
      {bottomPlaceholderContext && (
        <Placeholder
          context={bottomPlaceholderContext}
          placeholderHeight={placeholderHeight}></Placeholder>
      )}
      {renderLoadMore && loadMoreIndicator}
    </div>
  );
}
